import {
  segmentation as cstSegmentation,
  Enums as csToolsEnums,
} from "@cornerstonejs/tools";
import { getRenderingEngine } from "@cornerstonejs/core";
import { proxy, subscribe } from "valtio";
import dcmjs from "dcmjs";
import getIds from "lib/cornerstone/helpers/getIds";
import { groupBy } from "shared/utils/recursive";
import { devtools } from "valtio/utils";
import { lab2rgba } from "shared/utils/color";
import settingsState, { registerRoiOpacityCallback } from "stores/settings";
import {
  isPetModality,
  type PetImage,
  type PetMask,
} from "lib/cornerstone/helpers/niftiDiscovery";

// ============ NIfTI support ============

export interface NiftiMetadata {
  SeriesInstanceUID: string;
  StudyInstanceUID: string;
  Modality: string;
  SeriesDescription?: string;
  _niftiUrl: string;
  _niftiSegUrl?: string;
  _niftiSegUrls?: Record<string, string>;
  _activeModality?: string;
}

export function buildNiftiMetadata(
  niftiUrl: string,
  opts?: {
    studyId?: string;
    segUrl?: string;
    segUrls?: Record<string, string>;
    modality?: string;
  },
): NiftiMetadata {
  return {
    SeriesInstanceUID: niftiUrl,
    StudyInstanceUID: opts?.studyId ?? "nifti-study",
    Modality: isPetModality(opts?.modality) ? "PT" : "MR",
    SeriesDescription: "NIfTI",
    _niftiUrl: niftiUrl,
    _niftiSegUrl: opts?.segUrl,
    _niftiSegUrls: opts?.segUrls,
    _activeModality: opts?.modality,
  };
}

export function isNiftiMetadata(meta: any): meta is NiftiMetadata {
  return meta != null && typeof meta === "object" && "_niftiUrl" in meta;
}

export interface Segment extends dcmjs.data.SegMetadata {
  selected?: boolean;
  representationUID?: string;
  seriesUID?: string;
}

export interface ViewerState {
  viewerId: string | null;
  instanceMetadata: dcmjs.data.NaturalizedDataset | null;
  segmentsPerSeries: Segment[];
  editSegmentNumber?: number | null;
  niftiDatasets: any[];
  niftiBases: Record<string, string>;
  niftiActiveModality: string | null;
  niftiPetImages: PetImage[];
  niftiPetMasks: PetMask[];
  focusedMarkerCode: string | null;
  pendingSeriesUID: string | null;
}

const viewerState = proxy<ViewerState>({
  viewerId: null,
  instanceMetadata: null,
  segmentsPerSeries: [],
  niftiDatasets: [],
  niftiBases: {},
  niftiActiveModality: null,
  niftiPetImages: [],
  niftiPetMasks: [],
  focusedMarkerCode: null,
  pendingSeriesUID: null,
});

devtools(viewerState, { name: "viewer state", enabled: true });

// ============================================================================
// Core Fix: Local LUT Cache
// Maintains a 65536-length safe LUT for EVERY unique viewport representation
// to prevent "colorReference is null" crashes and ensure 100% sync.
// ============================================================================
const _localLutCache = new Map<number, [number, number, number, number][]>();
const SAFE_LUT_LENGTH = 65536;

// ============ Callbacks ============

registerRoiOpacityCallback(() => {
  if (viewerState.viewerId) {
    updateSegmentationVisibility().catch(console.error);
  }
});

subscribe(viewerState, (ops) => {
  if (!viewerState.viewerId) return;

  const seriesChange = ops.find((op) => op[1].includes("instanceMetadata"));
  if (
    seriesChange &&
    seriesChange[3] &&
    !seriesChange[1].includes("SegmentSequence")
  ) {
    const previousSeries = seriesChange[3] as dcmjs.data.NaturalizedDataset;
    const currentSeries = seriesChange[2] as dcmjs.data.NaturalizedDataset;
    onSeriesUpdate(previousSeries, currentSeries);
  }
});

function onSeriesUpdate(
  previousSeries: dcmjs.data.NaturalizedDataset,
  currentSeries: dcmjs.data.NaturalizedDataset,
) {
  if (isNiftiMetadata(currentSeries)) return;

  const { viewportId1, viewportId2, viewportId3 } = getIds(
    viewerState.viewerId,
  );
  const viewportIds = [viewportId1, viewportId2, viewportId3];

  const hasRepresentations = viewportIds.some((vpId) => {
    try {
      const reps =
        cstSegmentation.state.getSegmentationRepresentations(vpId) || [];
      return reps.length > 0;
    } catch {
      return false;
    }
  });

  if (hasRepresentations) {
    cstSegmentation.removeAllSegmentationRepresentations();
    _localLutCache.clear();
  }

  viewerState.segmentsPerSeries = viewerState.segmentsPerSeries.map((seg) => {
    if (seg.seriesUID !== previousSeries.SeriesInstanceUID) return seg;
    return { ...seg, selected: false };
  });
}

// ============ Internal helpers ============

function hasRepresentationOnViewport(
  viewportId: string,
  segmentationId: string,
): boolean {
  try {
    const reps =
      cstSegmentation.state.getSegmentationRepresentations(viewportId) || [];
    return reps.some((rep: any) => rep.segmentationId === segmentationId);
  } catch (e) {
    return false;
  }
}

function reapplyGlobalLabelmapStyle() {
  const opacity = settingsState.roiOpacity / 100;
  try {
    cstSegmentation.config.style.setStyle(
      { type: csToolsEnums.SegmentationRepresentations.Labelmap },
      {
        renderOutline: false,
        outlineWidth: 0,
        outlineWidthInactive: 0,
        renderOutlineInactive: false,
        fillAlpha: opacity,
        fillAlphaInactive: opacity,
      },
    );
  } catch {}
}

// ============ Public API ============

export function clearSeriesColors(seriesUID: string) {
  if (!viewerState.viewerId) return;

  const { viewportId1, viewportId2, viewportId3 } = getIds(
    viewerState.viewerId,
  );
  const viewportIds = [viewportId1, viewportId2, viewportId3];

  const rSeg = groupBy(
    viewerState.segmentsPerSeries.filter((s) => s.seriesUID === seriesUID),
    (s) => s.representationUID,
  );

  for (const [segmentationId, segments] of Object.entries(rSeg)) {
    if (!segmentationId || segmentationId === "undefined") continue;

    // 1. Gather all LUT indices assigned across all 3 viewports for this segmentation
    const lutIndices = new Set<number>();
    for (const viewportId of viewportIds) {
      const reps =
        cstSegmentation.state.getSegmentationRepresentations(viewportId) || [];
      const rep = reps.find((r: any) => r.segmentationId === segmentationId);
      if (rep && rep.colorLUTIndex !== undefined) {
        lutIndices.add(rep.colorLUTIndex);
      }
    }

    // 2. Clear colors safely in our local memory cache
    let isModified = false;
    for (const lutIndex of lutIndices) {
      const lut = _localLutCache.get(lutIndex);
      if (!lut) continue;

      for (const segment of segments) {
        const segNum = Number(segment.SegmentNumber);
        if (!segNum) continue;
        if (lut[segNum][3] !== 0) {
          lut[segNum] = [0, 0, 0, 0];
          isModified = true;
        }
      }

      if (isModified) {
        cstSegmentation.config.color.addColorLUT(lut, lutIndex);
      }
    }

    if (isModified) {
      try {
        cstSegmentation.triggerSegmentationEvents.triggerSegmentationModified(
          segmentationId,
        );
      } catch {}
    }
  }
}

// Debounce lock
let visibilityUpdatePromise: Promise<void> | null = null;
let pendingVisibilityUpdate = false;

export async function updateSegmentationVisibility(): Promise<void> {
  if (visibilityUpdatePromise) {
    pendingVisibilityUpdate = true;
    return visibilityUpdatePromise;
  }

  visibilityUpdatePromise = performVisibilityUpdate().finally(() => {
    visibilityUpdatePromise = null;
    if (pendingVisibilityUpdate) {
      pendingVisibilityUpdate = false;
      updateSegmentationVisibility();
    }
  });

  return visibilityUpdatePromise;
}

/**
 * Ultimate Multi-Viewport Synchronization
 */
async function performVisibilityUpdate() {
  if (!viewerState.viewerId) return;

  const { viewportId1, viewportId2, viewportId3, renderingEngineId } = getIds(
    viewerState.viewerId,
  );
  const viewportIds = [viewportId1, viewportId2, viewportId3];

  const rSeg = groupBy(
    viewerState.segmentsPerSeries,
    (s) => s.representationUID,
  );
  const entries = Object.entries(rSeg).filter(
    ([segmentationId]) => segmentationId && segmentationId !== "undefined",
  );
  const pendingSeriesUID = viewerState.pendingSeriesUID;

  let needsStyleApply = false;

  for (const [segmentationId, segments] of entries) {
    const selectedSegmentNumbers = segments
      .filter((s) => !!s.selected)
      .map((s) => Number(s.SegmentNumber));

    if (
      pendingSeriesUID !== null &&
      selectedSegmentNumbers.length > 0 &&
      segments.some((s) => s.seriesUID === pendingSeriesUID)
    ) {
      continue;
    }

    // =======================================================
    // Phase 1: Mount representations on ALL viewports
    // =======================================================
    for (const viewportId of viewportIds) {
      if (!hasRepresentationOnViewport(viewportId, segmentationId)) {
        try {
          await cstSegmentation.addSegmentationRepresentations(viewportId, [
            {
              segmentationId,
              type: csToolsEnums.SegmentationRepresentations.Labelmap,
            },
          ]);
          needsStyleApply = true;
        } catch (e) {
          console.error(
            `[SEG] Failed to mount ${segmentationId} on ${viewportId}`,
            e,
          );
        }
      }
    }

    if (needsStyleApply) {
      reapplyGlobalLabelmapStyle();
    }

    // =======================================================
    // Phase 2: Collect ALL LUT Indices used by the 3 viewports
    // Because each viewport creates its own LUT array internally!
    // =======================================================
    const lutIndices = new Set<number>();
    for (const viewportId of viewportIds) {
      const reps =
        cstSegmentation.state.getSegmentationRepresentations(viewportId) || [];
      const rep = reps.find((r: any) => r.segmentationId === segmentationId);
      if (rep && rep.colorLUTIndex !== undefined) {
        lutIndices.add(rep.colorLUTIndex);
      }
    }

    // =======================================================
    // Phase 3: Update ALL associated LUT arrays in memory
    // =======================================================
    const alpha = Math.round((settingsState.roiOpacity / 100) * 255);
    let isModified = false;

    for (const lutIndex of lutIndices) {
      // Ensure the safe 65536 array exists
      if (!_localLutCache.has(lutIndex)) {
        const emptyLut = Array.from(
          { length: SAFE_LUT_LENGTH },
          () => [0, 0, 0, 0] as [number, number, number, number],
        );
        _localLutCache.set(lutIndex, emptyLut);
        isModified = true; // Needs injection
      }

      const lut = _localLutCache.get(lutIndex)!;

      for (const segment of segments) {
        if (!segment || !segment.SegmentNumber) continue;

        const segNum = segment.SegmentNumber;
        const isVisible = selectedSegmentNumbers.includes(segNum);

        let targetColor: [number, number, number, number] = [0, 0, 0, 0];
        if (isVisible) {
          if (segment.RecommendedDisplayCIELabValue) {
            const rgba = lab2rgba(segment.RecommendedDisplayCIELabValue) as [
              number,
              number,
              number,
              number,
            ];
            targetColor = [rgba[0], rgba[1], rgba[2], alpha];
          } else if ((segment as any)._rgbColor) {
            const [r, g, b] = (segment as any)._rgbColor;
            targetColor = [r, g, b, alpha];
          } else {
            targetColor = [255, 255, 255, alpha];
          }
        }

        // Only update memory if the color actually changed
        if (
          lut[segNum][0] !== targetColor[0] ||
          lut[segNum][1] !== targetColor[1] ||
          lut[segNum][2] !== targetColor[2] ||
          lut[segNum][3] !== targetColor[3]
        ) {
          lut[segNum] = targetColor;
          isModified = true;
        }
      }

      // Inject the fully updated array back into the engine synchronously
      if (isModified) {
        try {
          cstSegmentation.config.color.addColorLUT(lut, lutIndex);
        } catch (e) {}
      }
    }

    // =======================================================
    // Phase 4: Notify GPU (Triggered ONLY ONCE to prevent race conditions)
    // =======================================================
    if (isModified) {
      try {
        cstSegmentation.triggerSegmentationEvents.triggerSegmentationModified(
          segmentationId,
        );
      } catch (e) {}
    }
  }

  // Force render
  const renderingEngine = getRenderingEngine(renderingEngineId);
  if (renderingEngine) {
    renderingEngine.renderViewports(viewportIds);
  }
}

export function setEditSegment(segment: Segment) {
  if (viewerState.editSegmentNumber === segment.SegmentNumber) {
    viewerState.editSegmentNumber = null;
  } else {
    viewerState.editSegmentNumber = segment.SegmentNumber;
  }
}

export default viewerState;
